// Een kaart op het bord, met de markup van het prototype (CARD in design/build/prototype.js).
// Uren uit Clockify (gelogd / schatting) staan er alleen bij een gekoppelde taak; loopt er een timer op de taak, dan
// staat bovenaan wie eraan bezig is en sinds hoelang. Het bord leest Clockify één keer en geeft het door (live), zodat
// niet elke kaart een eigen verversing start. Zonder Clockify of tijdens het laden toont de kaart gewoon geen uren.
import { useEffect, useState, type MouseEvent, type PointerEvent } from "react";
import { fmtSince, loadTone, useTimer, weekLoad, type Capacity, type ClockifyLive, type ClockifyStatus, type TaskHours, type Timer } from "../lib/api-clockify";
import { cardView, labelColor } from "../lib/board";
import { Icon } from "../lib/icons";
import type { PlanningData, Task } from "../lib/types";

// ---------- gedeeld met het bord en de taaklade ----------

/** Clockify is gekoppeld en heeft minstens één ronde gelezen; anders zijn uren en bezetting nog niet te vertrouwen. */
export const clockifyReady = (s: ClockifyStatus | undefined) => !!s && s.state !== "uit" && !!s.lastOk;

/**
 * Loopt deze timer op deze taak? Op een gekoppelde Clockify-taak zegt de server welke Planner-taak het is; zonder
 * Clockify-taak start de app de timer op het project met de titel als omschrijving.
 */
// een timer op een gedeelde Clockify-taak die niet in de app startte, hoort bij elke taak die ze deelt
export const timerOnTask = (t: Timer, task: Task) => t.taskId === task.id || (!t.taskId && !!t.taskIds?.includes(task.id))
  || (!t.taskId && !t.clockifyTaskId && t.description === task.title);

/** Nu, elke halve minuut opnieuw, voor de duur van een timer. */
export function useNow(ms = 30e3) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const i = window.setInterval(() => setNow(Date.now()), ms);
    return () => window.clearInterval(i);
  }, [ms]);
  return now;
}

export interface Thresholds { room: number; full: number; over: number }
/** Drempels uit Instellingen (zelfde standaard als de server). */
export const thresholdsOf = (s: Partial<Thresholds> | undefined): Thresholds => ({ room: s?.room ?? 50, full: s?.full ?? 90, over: s?.over ?? 100 });

export interface Load { pct: number | null; barCls: string; barW: number; textCls: string; text: string; absent: string | null }

/**
 * Bezetting van een persoon deze week, zoals op Capaciteit: geplande uren met de achterstand over vier weken en support
 * als vaste reservering. Alleen afwijkingen krijgen kleur. null zonder (betrouwbare) capaciteit.
 */
export function loadOf(cap: Capacity | undefined, personId: string, th: Thresholds): Load | null {
  if (!cap || !clockifyReady(cap.clockify)) return null;
  const p = cap.people.find((x) => x.personId === personId);
  if (!p || !p.weeks.length) return null;
  const { pct } = weekLoad(p, 0, { backlogWeeks: 4, withSupport: true });
  const tone = loadTone(pct, th);
  const kinds = p.weeks[0].absent.map((a) => a.kind);
  const absent = pct != null ? null : kinds.includes("verlof") ? "met verlof" : kinds.length ? "afwezig" : "zonder uren";
  return {
    pct, absent,
    barCls: "p-load " + ({ overvol: "err", vol: "warn", ruimte: "low", normaal: "ok", afwezig: "" } as const)[tone],
    barW: pct == null ? 0 : Math.min(100, pct),
    textCls: tone === "overvol" ? "t-err" : tone === "vol" ? "t-warn" : pct == null ? "t-mute" : "",
    text: pct == null ? (kinds.includes("verlof") ? "verlof" : kinds.length ? "afwezig" : "–") : pct + " %",
  };
}

const num = (h: number) => (Math.round(h * 10) / 10).toLocaleString("nl-BE", { maximumFractionDigits: 1 });

/** Uren op de kaart: gelogd / schatting, of geen schatting; niets zonder gekoppelde Clockify-taak. */
function hoursOf(h: TaskHours | undefined): { cls: string; text: string } | null {
  if (!h || h.none || h.missing) return null;
  if (h.estimate == null) return { cls: "p-meta-i t-warn", text: "geen schatting" };
  const logged = h.logged || 0;
  return { cls: "p-meta-i" + (logged > h.estimate ? " t-err" : ""), text: num(logged) + " / " + num(h.estimate) + " u" };
}

// ---------- de kaart ----------

interface Props {
  task: Task;
  data: PlanningData;
  inGroup: boolean;
  klantName: string;
  draggable?: boolean;
  /** Clockify zoals het bord het leest; zonder: geen uren en geen timer op de kaart */
  live?: ClockifyLive | null;
  /** aangemelde gebruiker, om de eigen timer te kunnen stoppen */
  meId?: string;
  onOpen: () => void;
  onPointerDown?: (e: PointerEvent<HTMLButtonElement>) => void;
}

export function TaskCard({ task, data, inGroup, klantName, draggable, live, meId, onOpen, onPointerDown }: Props) {
  const v = cardView(task, data, inGroup, klantName);
  const on = live && clockifyReady(live.clockify) ? live : null;
  const hours = on ? hoursOf(on.tasks[task.id]) : null;
  const timers = on ? on.timers.filter((t) => timerOnTask(t, task)) : [];
  return (
    <button type="button" className={"p-task" + (draggable ? " can-drag" : "") + (v.pending ? " pending" : "") + (timers.length ? " live" : "")} data-task={task.id} onClick={onOpen} onPointerDown={onPointerDown}>
      {timers.length > 0 && <LiveLine timers={timers} data={data} task={task} meId={meId} />}
      <span className="tt">{task.title}</span>
      <span className="mt">
        {v.crit && <span className="p-crit">Kritisch</span>}
        {v.prio && <span className={v.prioCls}>{v.prio}</span>}
        {v.dl && (
          <span className={v.dlCls}>
            {v.dlCls.startsWith("p-dl") && <Icon name={v.dl.key === "verlopen" ? "alert" : "cal"} size={13} />}
            {v.dlText}
          </span>
        )}
        {v.lbl && <span className="p-lbl"><i style={{ background: labelColor(v.lbl) }} />{v.lbl}</span>}
        {hours && <span className={hours.cls}><Icon name="clock" size={13} />{hours.text}</span>}
        {v.checklist && <span className="p-meta-i"><Icon name="checklist" size={13} />{v.checklist}</span>}
        {v.deps > 0 && <span className="p-meta-i"><Icon name="arrow" size={13} />wacht op {v.deps}</span>}
        {v.atts > 0 && <span className="p-meta-i"><Icon name="clip" size={13} />{v.atts}</span>}
        {v.pending && <span className="p-meta-i t-mute" title="Planner verwerkt de wijziging">bezig met opslaan</span>}
      </span>
    </button>
  );
}

// Bezig · Jens · 43 min, met een stopknop als het uw eigen timer is. Alleen kaarten met een timer tellen de minuten.
function LiveLine({ timers, data, task, meId }: { timers: Timer[]; data: PlanningData; task: Task; meId?: string }) {
  const now = useNow();
  const first = (pid: string) => (pid === meId ? "u" : data.people.find((p) => p.id === pid)?.first || "iemand");
  const names = timers.map((t) => first(t.personId));
  const who = names.length < 2 ? names[0] : names.slice(0, -1).join(", ") + " en " + names[names.length - 1];
  const since = timers.reduce((a, t) => (Date.parse(t.since) < Date.parse(a) ? t.since : a), timers[0].since);
  const stale = timers.some((t) => t.stale);
  const mine = !!meId && timers.some((t) => t.personId === meId);
  return (
    <span className={"p-task-live" + (stale ? " t-warn" : "")} title={stale ? "Deze timer loopt al meer dan 12 uur: wellicht vergeten" : undefined}>
      <span className={"ex-dot ex-dot--" + (stale ? "warn" : "ok")} />Bezig · {who.charAt(0).toUpperCase() + who.slice(1)} · {fmtSince(since, now)}
      {mine && <StopMine title={task.title} />}
    </span>
  );
}

// Binnen de kaart (zelf een knop) geen tweede knop: een klikvlak dat de kaart niet opent. Met het toetsenbord stopt u
// de timer in de taaklade.
function StopMine({ title }: { title: string }) {
  const timer = useTimer();
  const stop = (e: MouseEvent) => { e.stopPropagation(); if (!timer.busy) timer.stop(); };
  return <span className="p-task-stop p-ck-stop" aria-hidden="true" title={"Uw timer op " + title + " stoppen"} onClick={stop}><Icon name="stop" size={12} /></span>;
}
